import { createEffect, createMemo, createSignal, on } from "solid-js"
import type { RadioGroupOption } from "@/designSystem/components/radio-group"
import { RadioGroup } from "@/designSystem/components/radio-group"
import { useI18nContext } from "../../i18n/i18n-solid"

export type AudienceType = "everyone" | "all_subscribers"

export type AudienceSettings = {
  private: boolean
  dirty: boolean
}

export interface AudienceFormPost {
  private?: boolean | null
  tiers?: Array<{ id: string }> | null
}

type AudienceFormProps = {
  copy?: "post" | "form"
  post: AudienceFormPost
  onAudienceChange?: (audience: AudienceSettings) => void
}

export function AudienceForm(props: AudienceFormProps) {
  const { LL } = useI18nContext()
  const [audienceType, setAudienceType] = createSignal<AudienceType>(
    props.post.private ? "all_subscribers" : "everyone",
  )

  createEffect(
    on(
      () => props.post,
      post => setAudienceType(post.private ? "all_subscribers" : "everyone"),
    ),
  )

  const audienceSettings = createMemo((): AudienceSettings => {
    const isPrivate = audienceType() === "all_subscribers"
    return {
      private: isPrivate,
      dirty:
        Boolean(props.post.private) !== isPrivate ||
        (props.post.tiers?.length ?? 0) > 0,
    }
  })

  createEffect(
    on(audienceSettings, settings => props.onAudienceChange?.(settings), {
      defer: true,
    }),
  )

  const audienceOptions = createMemo((): RadioGroupOption[] => {
    if (props.copy === "form")
      return [
        {
          value: "everyone",
          label: LL().blog.formsPage.everyone(),
          description: LL().blog.formsPage.everyoneDescription(),
        },
        {
          value: "all_subscribers",
          label: LL().blog.formsPage.subscribersOnly(),
          description: LL().blog.formsPage.subscribersOnlyDescription(),
        },
      ]

    return [
      {
        value: "everyone",
        label: LL().postSettings.audienceEveryoneLabel(),
        description: LL().postSettings.audienceEveryoneDescription(),
      },
      {
        value: "all_subscribers",
        label: LL().postSettings.audienceAllSubscribersLabel(),
        description: LL().postSettings.audienceAllSubscribersDescription(),
      },
    ]
  })

  return (
    <RadioGroup
      value={audienceType()}
      onChange={value => setAudienceType(value as AudienceType)}
      options={audienceOptions()}
      hue="amber"
    />
  )
}
